import { filter } from "lodash";
import { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
// material
import {
  Card,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
// components
import Page from "../../components/Page";
import Scrollbar from "../../components/Scrollbar";
import SearchNotFound from "../../components/SearchNotFound";
import {
  TableTitle,
  TableHeader,
} from "../../components/_dashboard/user";
//
import { useTranslation } from "react-i18next";
import { db } from "../../database/db";
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from "firebase/firestore";

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) => _user.username.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function PendingMoneyRequest() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("username");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [requestList, setRequestList] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const TABLE_HEAD = [
    { id: "sno", label: "#", alignRight: false },
    { id: "username", label: t("User_Name"), alignRight: false },
    { id: "email", label: t("Email"), alignRight: false },
    { id: "paypal", label: "PayPal Email", alignRight: false },
    { id: "tokens", label: t("Stars"), alignRight: false },
    { id: "gross", label: t("Total_Amount"), alignRight: false },
    { id: "net", label: t("Payable_Amount"), alignRight: false },
    { id: "action", label: t("Action"), alignRight: false },
  ];

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "PayoutRequests"), where("status", "==", "pending"));
      const querySnapshot = await getDocs(q);
      const list = [];
      
      for (const docSnapshot of querySnapshot.docs) {
        const data = docSnapshot.data();
        // Fetch user info for name
        const userDoc = await getDoc(doc(db, "User", data.userId));
        const userData = userDoc.exists() ? userDoc.data() : {};
        
        list.push({
          id: docSnapshot.id,
          username: userData.name || "Unknown",
          email: userData.email || "N/A",
          ...data
        });
      }
      setRequestList(list);
    } catch (error) {
      console.error("Error fetching payout requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (id, status) => {
    try {
      const docRef = doc(db, "PayoutRequests", id);
      await updateDoc(docRef, { status });
      toast.success(`Request ${status === 'completed' ? 'Approved' : 'Declined'} successfully`);
      fetchRequests();
    } catch (error) {
      console.error("Error updating request:", error);
      toast.error("Error updating request status");
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = requestList.map((n) => n.username);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - requestList.length) : 0;

  const filteredRequests = applySortFilter(
    requestList,
    getComparator(order, orderBy)
  );

  const isNotFound = filteredRequests.length === 0 && !loading;

  const TableCellStyles = styled((props) => <TableCell {...props} />)(
    ({ theme }) => ({
      border: "1px solid #e3e6f0",
      fontSize: "12px",
    })
  );

  return (
    <Page title="Money Request | Pending Request">
      <ToastContainer />
      <Container>
        <Card sx={{ borderRadius: "5px" }}>
          <TableTitle title={t("Pending_Request")} />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, p: "18px" }}>
              <Table>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={requestList.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredRequests
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      const {
                        id,
                        username,
                        email,
                        paypalEmail,
                        tokens,
                        grossAmount,
                        netAmount,
                      } = row;
                      const isItemSelected = selected.indexOf(username) !== -1;

                      return (
                        <TableRow
                          hover
                          key={id}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                        >
                          <TableCellStyles align="left">
                            {index + 1}
                          </TableCellStyles>
                          <TableCellStyles align="left">
                            {username}
                          </TableCellStyles>
                          <TableCellStyles align="left">
                            {email}
                          </TableCellStyles>
                          <TableCellStyles align="left">
                            {paypalEmail}
                          </TableCellStyles>
                          <TableCellStyles align="left">
                            {tokens}
                          </TableCellStyles>
                          <TableCellStyles align="left">
                            ${grossAmount?.toFixed(2)}
                          </TableCellStyles>
                          <TableCellStyles align="left">
                            ${netAmount?.toFixed(2)}
                          </TableCellStyles>
                          <TableCellStyles align="left">
                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="contained"
                                onClick={() => handleAction(id, "completed")}
                                sx={{
                                  borderRadius: "6px",
                                  boxShadow: "none",
                                  backgroundColor: "info.main",
                                }}
                              >
                                {t("Paypal")}
                              </Button>
                              <Button
                                variant="contained"
                                onClick={() => handleAction(id, "declined")}
                                sx={{
                                  borderRadius: "6px",
                                  boxShadow: "none",
                                  backgroundColor: "#e74a3b",
                                }}
                              >
                                {t("Decline")}
                              </Button>
                            </Stack>
                          </TableCellStyles>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCellStyles colSpan={8} />
                    </TableRow>
                  )}
                </TableBody>
                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCellStyles
                        align="center"
                        colSpan={8}
                        sx={{ py: 3 }}
                      >
                        <SearchNotFound />
                      </TableCellStyles>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Container>
    </Page>
  );
}
